import React, {useCallback, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import PropTypes from "prop-types";
import classnames from "classnames";
import {useField} from "formik";
import {trim} from "@awe-web/shared/lib";
import {getCreditCards} from "@awe-web/shared/lib";
import {Translation as T, useTranslation} from "@awe-web/shared/lib";
import {Checkbox, RadioButton} from "@awe-web/shared/lib";

import {Button} from "../../components/form.js";
import {Overlay, OverlayHeading, OverlaySection} from "../../components/overlay.js";
import {showCreditCardForm, deleteCreditCard} from "../actions.js";

import "./credit_cards.scss";

const VALID_TILL = "FSD0000171";
const REMOVE_CREDIT_CARD = "FSD0000220";
const CREDIT_CARD_IMAGE = "FSD0000349";

const CREDIT_CARD_CONFIRMATION = "FSD0000221";
const ADD_CREDIT_CARD = "FSD0000222";

const DELETE_CREDIT_CARD = "FSD0000220";
const DELETE_CREDIT_CARD_CONFIRMATION = "FSD0000300";
const DELETE_CREDIT_CARD_CONFIRM = "FSD0000299";
const DELETE_CREDIT_CARD_CANCEL = "FSD0000301";

function DeleteCreditCardConfirmation({creditCard, closeOverlay}) {
  const dispatch = useDispatch();

  const onDeleteClicked = useCallback(() => {
    closeOverlay();
    dispatch(deleteCreditCard(creditCard.id));
  }, [closeOverlay, creditCard.id, dispatch]);

  const onCancelClicked = useCallback(() => {
    closeOverlay();
  }, [closeOverlay]);

  return (
    <>
      <OverlayHeading><T code={DELETE_CREDIT_CARD} /></OverlayHeading>
      <OverlaySection>
        <p className="delete-credit-card-confirmation__notice"><T code={DELETE_CREDIT_CARD_CONFIRMATION} /></p>
        <CreditCard className="delete-credit-card-confirmation__credit-card" creditCard={creditCard} />
        <div className="delete-credit-card-confirmation__buttons">
          <Button primary continue className="delete-credit-card-confirmation__confirm-button" onClick={onDeleteClicked}>
            <T code={DELETE_CREDIT_CARD_CONFIRM} />
          </Button>
          <Button secondary back onClick={onCancelClicked}><T code={DELETE_CREDIT_CARD_CANCEL} /></Button>
        </div>
      </OverlaySection>
    </>
  );
}

function DeleteCreditCardButton({creditCard}) {
  const overlayRef = useRef();

  const openOverlay = useCallback(() => {
    if (overlayRef.current) {
      overlayRef.current.openOverlay();
    }
  }, []);

  return (
    <>
      <Button tertiary className="delete-credit-card-button" onClick={openOverlay}>
        <T code={REMOVE_CREDIT_CARD} />
      </Button>
      <Overlay ref={overlayRef} className="delete-credit-card-button__overlay">
        {({closeOverlay}) => (
          <DeleteCreditCardConfirmation
            creditCard={creditCard}
            closeOverlay={closeOverlay}
          />
        )}
      </Overlay>
    </>
  );
}

function CreditCard({withDeleteButton, creditCard}) {
  const {translate} = useTranslation();
  const creditCardImage = trim(translate(CREDIT_CARD_IMAGE));

  return (
    <div className="credit-card credit-card-list__credit-card" >
      <div className="credit-card__image-wrapper">
        <div
          className="credit-card__image-wrapper__image"
          style={{backgroundImage: `url(${creditCardImage})`}} />
      </div>
      <div className="credit-card__body">
        <div className="credit-card__number">
          <div>{creditCard?.creditcardbrand}</div>
          <div>{creditCard?.cardnumber}</div>
        </div>
        <div className="credit-card__valid-till">
          <T className="credit-card__valid-till__label" code={VALID_TILL} />
          <div>{creditCard?.validTill}</div>
        </div>
        {withDeleteButton
          ? <DeleteCreditCardButton creditCard={creditCard} />
          : null
        }
      </div> 
    </div>
  );
}

CreditCard.propTypes = {
  withDeleteButton: PropTypes.bool,
  creditCard: PropTypes.shape({
    id: PropTypes.number,
    creditcardbrand: PropTypes.string,
    cardnumber: PropTypes.string,
    validTill: PropTypes.string
  })
};

export function CreditCardList() {
  const stateCreditCards = useSelector((state) => getCreditCards(state));

  const creditCards = useMemo(() => {
    return stateCreditCards.toJS();
  }, [stateCreditCards]);

  return (
    <div className="credit-cards-fieldset__credit-card-list credit-card-list">
      {creditCards.map(creditCard => (
        <CreditCard
          key={creditCard.id}
          withDeleteButton
          creditCard={creditCard}
        />
      ))}
    </div>
  );
}

export function AddCreditCard({buttonClassName, className}) {
  const dispatch = useDispatch();
  const [isChecked, setIsChecked] = useState(false);
  const combinedClassName = classnames("add-credit-card", className);

  const onChange = useCallback(({target: {checked}}) => {
    setIsChecked(checked);
  }, []);

  const onClick = useCallback(async () => {
    setIsChecked(false);
    dispatch(showCreditCardForm());
  }, [dispatch]);

  return (
    <div className={combinedClassName}>
      <label className="labeled-checkbox add-credit-card__labeled-checkbox">
        <Checkbox checked={isChecked} onChange={onChange} className="labeled-checkbox__input"/>
        <T className="checkbox-label" code={CREDIT_CARD_CONFIRMATION} />
      </label>
      <div className="add-credit-card__button-container">
        <Button secondary continue disabled={!isChecked} className={buttonClassName} onClick={onClick}>
          <T code={ADD_CREDIT_CARD} />
        </Button>
      </div>
    </div>
  );
}

AddCreditCard.propTypes = {
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  showCreditCardForm: PropTypes.func
};

export function CreditCardRadioButtons({name, className, creditCards}) {
  const combinedClassName = classnames("credit-card-radio-buttons", className);
  
  const [{value, onChange}] = useField(name);
  
  const handleChange = event => {
    const {target} = event;
    onChange({target: {name: target.name, value: Number(target.value)}});
  };
  
  if (!creditCards || creditCards.length === 0) {
    return null;
  }

  return (
    <div className={combinedClassName}>
      {creditCards.map(({id, cardnumber, validTill}) => (
        <label key={id} className="labeled-radio-button">
          <RadioButton
            className="labeled-radio-button__input"
            name={name}
            value={id}
            checked={id === value}
            onChange={handleChange}
          />
          <div>
            <div className="credit-card-radio-buttons__card-number radiobutton-label">{cardnumber}</div>
            <div>{validTill}</div>
          </div>
        </label>
      ))}
    </div>
  );
}

CreditCardRadioButtons.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  creditCards: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    cardnumber: PropTypes.string,
    validTill: PropTypes.string
  }))
};
